import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Bell,
  Users,
  ScrollText,
  ChevronLeft,
  ChevronRight,
  Monitor,
  AlertTriangle,
  RefreshCw,
  Settings,
} from "lucide-react";
import { AgencyLogo } from "@/components/agency-logo";
import { cn } from "@/lib/utils";
import { isAgencyUser, isSuperAdmin } from "@/lib/roles";
import { useAuth } from "@/stores/auth-store";

const mainNav = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/" },
  { label: "Tenants", icon: Building2, href: "/tenants" },
  { label: "Alerts", icon: Bell, href: "/alerts" },
  { label: "Sync", icon: RefreshCw, href: "/sync" },
];

const adminNav = [
  { label: "Users", icon: Users, href: "/admin/users" },
  { label: "Audit Logs", icon: ScrollText, href: "/admin/audit-logs" },
];

const tenantNav = [
  { label: "Overview", icon: LayoutDashboard, href: "" },
  { label: "Devices", icon: Monitor, href: "/devices" },
  { label: "Users", icon: Users, href: "/users" },
  { label: "Alerts", icon: AlertTriangle, href: "/alerts" },
  { label: "Settings", icon: Settings, href: "/settings" },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { tenantId } = useParams();
  const { user } = useAuth();
  const agency = isAgencyUser(user);
  const multiTenantClient = !agency && (user?.tenant_ids?.length ?? 0) > 1;
  const activeTenantId = tenantId || (!agency && user?.tenant_ids?.[0]) || null;
  const logoHref = agency ? "/" : multiTenantClient ? "/tenants" : `/tenants/${user?.tenant_ids?.[0] ?? ""}`;

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-border bg-surface/80 transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex h-14 items-center justify-between px-4">
        <Link to={logoHref} className="flex items-center">
          {collapsed ? (
            <AgencyLogo iconOnly className="h-6 w-6 text-text-primary" />
          ) : (
            <AgencyLogo className="h-5 text-text-primary" />
          )}
        </Link>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="rounded p-1 text-text-muted hover:bg-surface-hover hover:text-text-primary"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {agency && (
          <div className="space-y-1">
            {mainNav.map((item) => (
              <NavItem key={item.href} item={item} collapsed={collapsed} active={location.pathname === item.href} />
            ))}
          </div>
        )}

        {multiTenantClient && (
          <div className="space-y-1">
            <NavItem
              item={{ label: "Tenants", icon: Building2, href: "/tenants" }}
              collapsed={collapsed}
              active={location.pathname === "/tenants"}
            />
          </div>
        )}

        {activeTenantId && (
          <>
            {agency && (
              <div className="my-4 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            )}
            {!collapsed && (
              <p className="mb-2 px-3 text-xs font-medium uppercase text-text-muted">Tenant</p>
            )}
            <div className="space-y-1">
              {tenantNav.map((item) => {
                const href = `/tenants/${activeTenantId}${item.href}`;
                return (
                  <NavItem
                    key={item.href}
                    item={{ ...item, href }}
                    collapsed={collapsed}
                    active={location.pathname === href}
                  />
                );
              })}
            </div>
          </>
        )}

        {isSuperAdmin(user) && (
          <>
            <div className="my-4 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            {!collapsed && (
              <p className="mb-2 px-3 text-xs font-medium uppercase text-text-muted">Admin</p>
            )}
            <div className="space-y-1">
              {adminNav.map((item) => (
                <NavItem key={item.href} item={item} collapsed={collapsed} active={location.pathname === item.href} />
              ))}
            </div>
          </>
        )}
      </nav>
    </aside>
  );
}

function NavItem({
  item,
  collapsed,
  active,
}: {
  item: { label: string; icon: React.ElementType; href: string };
  collapsed: boolean;
  active: boolean;
}) {
  return (
    <Link
      to={item.href}
      className={cn(
        "flex items-center gap-3 px-3 py-2 text-sm transition-all",
        active
          ? "bg-accent/5 text-accent font-medium border-l-2 border-accent rounded-r-lg"
          : "text-text-secondary hover:bg-surface-hover hover:text-text-primary rounded-lg"
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
}
